import { Directive, HostListener, Optional } from "@angular/core";
import { FormGroupDirective } from "@angular/forms";

/**
 * Global "Enter to submit" behaviour for every reactive form.
 *
 * Auto-attaches to any `form[formGroup]` (no opt-in required) and
 * triggers the host form's existing `(ngSubmit)` handler when the user
 * presses Enter, with sensible exclusions:
 *
 * - Textareas keep their native newline behaviour.
 * - Buttons / links keep their native activation behaviour, so we don't
 *   double-fire when Enter "clicks" a focused button.
 * - When a Taiga UI combo box / data list already handled Enter to
 *   select an option, it calls `preventDefault()` first; we bail out via
 *   the `defaultPrevented` check so option selection still works.
 *
 * We use `FormGroupDirective.onSubmit()` so Angular flips the form's
 * `submitted` flag (which controls some validation-rendering paths) and
 * then emits `ngSubmit` exactly as a native form submission would.
 */
@Directive({
  selector: "form[formGroup]",
  standalone: true,
})
export class EnterSubmitDirective {
  constructor(
    @Optional() private readonly formGroupDirective: FormGroupDirective,
  ) {}

  @HostListener("keydown.enter", ["$event"])
  onEnter(event: Event): void {
    if (!this.formGroupDirective) return;
    if (event.defaultPrevented) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    const tag = target.tagName;

    if (tag === "TEXTAREA" || target.closest("textarea")) return;
    if (tag === "BUTTON" || tag === "A") return;

    if (tag === "INPUT") {
      const inputType = (target as HTMLInputElement).type;
      if (
        inputType === "button" ||
        inputType === "submit" ||
        inputType === "reset"
      ) {
        return;
      }
    }

    event.preventDefault();
    this.formGroupDirective.onSubmit(event);
  }
}
